import { ApiService } from './api';
import type { Event, EventFacebookPixel, EventPublicInfo, EventRegistrationAvailability, PaginatedResponse } from '@/types/api';
import { EventFilters, filtersToBackendParams } from '@/types/filters';
import { config } from '@/config/config';
import { isDevControlBypassed } from '@/lib/dev-control';
import type { Deporte } from '@/types/category';
import type { SeatAvailability, SeatAvailabilityEnvelope, StockAvailability } from '@/types/seat-reservation';

import { SPORT_NAME_ALIASES } from '@/utils/sport-aliases';

export type StockAvailabilityEnvelope =
  | { available: true; data: StockAvailability }
  | { available: false; reason: 'not_initialized' | 'service_down' };

// SPORT_NAME_ALIASES (workaround de datos sucios) vive en @/utils/sport-aliases para compartirse
// con los breadcrumbs de evento. La capa de consulta lo usa en normalizeFiltersForBackend: cuando
// el deporte tiene alias, emite `data.sport(s)` (IN) en vez de igualdad exacta. Notas de estado:
// "obstaculos" (116 Obstacles) es el caso real (publicados); "Otros"/"Running/Atletismo"/
// "running/athletics" son defensivos (0 publicados); "acuatlon"/"cuadriathlon" NO son alias
// (el diccionario inglés ya usa esas grafías).

// [devcontrol-004] SEMÁFORO TEMPORAL de migración — retirar cuando todos los eventos entren (ver CLAUDE.md).
// devId del development "árbol público /event/*" en el contenedor Cosmos public/devControl.
// Los eventos cuyo eventId (string) esté en el array `allowed` del doc usan el flujo nuevo;
// el resto sigue viajando al WebRTS legacy (www.rockthesport.com). Ver isEventOnNewFlow.
export const NEW_EVENT_FLOW_DEV_ID = '004';

export class EventService {
  private static instance: EventService;
  private api: ApiService;
  private englishTranslations: any = null;

  private constructor() {
    this.api = ApiService.getInstance();
    this.loadEnglishTranslations();
  }

  static getInstance(): EventService {
    if (!EventService.instance) {
      EventService.instance = new EventService();
    }
    return EventService.instance;
  }

  private async loadEnglishTranslations() {
    try {
      const translations = await import('@/locales/en/common.json');
      this.englishTranslations = translations.default.sportsTranslations;
    } catch (error) {
      console.error('Error loading English translations:', error);
    }
  }

  private async ensureTranslationsLoaded() {
    if (!this.englishTranslations) {
      await this.loadEnglishTranslations();
    }
  }

  /**
   * Gets the country ID from localStorage or returns the default value
   * Used by methods that need to filter by country (getFreshEvents, getTendencyEvents)
   * @returns The country ID from localStorage or default value from config
   */
  getCountryId(): string {
    if (typeof window !== 'undefined') {
      try {
        const savedCountry = localStorage.getItem('selectedCountry');
        if (savedCountry) {
          const parsedCountry = JSON.parse(savedCountry);
          return parsedCountry.id || config.app.defaultCountryId;
        }
      } catch (error) {
        console.error('Error reading country from localStorage:', error);
      }
    }
    return config.app.defaultCountryId;
  }

  async convertToEnglishSportName(sportId: string): Promise<string> {
    await this.ensureTranslationsLoaded();

    if (!this.englishTranslations || !this.englishTranslations.names) {
      return sportId.toLowerCase();
    }

    const englishName = this.englishTranslations.names[sportId];
    if (!englishName) {
      return sportId.toLowerCase();
    }

    return englishName.toLowerCase();
  }

  async convertToEnglishSubSportName(categories: string[], parentSportId: string): Promise<string[]> {
    await this.ensureTranslationsLoaded();

    if (!this.englishTranslations || !parentSportId) {
      return categories;
    }

    const subsportsForParent = this.englishTranslations.subsports?.[parentSportId];
    if (!subsportsForParent) {
      return categories;
    }

    const englishSubSportName = categories.map(c => subsportsForParent[c].toLowerCase());
    return englishSubSportName
  }

  private getDefaultOrderByAdded(filters: object) {
    for (const filter of Object.keys(filters)) {
      if (filter.includes("orderBy")) {
        return filters;
      }
    }

    return {
      ...filters,
      "(d) orderBy": "data.dates.startedDateTimestamp"
    };
  }

  /**
   * Normaliza los filtros para que tengan el formato correcto para Cosmos DB
   * - data.sport (array) → data.sport(s) (array con comillas)
   * - data.subsport (array) → data.subsports(multipleArrayContains) (string separado por comas)
   */
  private normalizeFiltersForBackend(filters: any): any {
    const normalized: any = { ...filters };

    // Expandir alias de deporte: si el nombre solicitado tiene grafías alternati-
    // vas almacenadas (ver SPORT_NAME_ALIASES), añadirlas para que la consulta
    // case con cualquiera de ellas (data.sport IN (...)). Sólo se convierte a la
    // forma de array/IN cuando un alias amplía el conjunto; en caso contrario se
    // mantiene el string (igualdad exacta) para no cambiar el resto de deportes.
    if (normalized['data.sport'] != null) {
      const requested: string[] = Array.isArray(normalized['data.sport'])
        ? normalized['data.sport'].map((v: any) => String(v))
        : [String(normalized['data.sport'])];

      const expanded = new Set<string>();
      for (const name of requested) {
        expanded.add(name);
        for (const alias of SPORT_NAME_ALIASES[name.toLowerCase()] ?? []) {
          expanded.add(alias);
        }
      }

      if (expanded.size > requested.length || Array.isArray(normalized['data.sport'])) {
        normalized['data.sport'] = Array.from(expanded);
      }
    }

    // Convertir data.sport (array) a data.sport(s) para que se formateen con comillas
    if (normalized['data.sport'] && Array.isArray(normalized['data.sport'])) {
      normalized['data.sport(s)'] = normalized['data.sport'];
      delete normalized['data.sport'];
    }

    // Convertir data.subsport (array) a data.subsports(multipleArrayContains) para búsqueda en arrays
    if (normalized['data.subsport'] && Array.isArray(normalized['data.subsport'])) {
      const subsportList = normalized['data.subsport']
        .map((item: any) => item?.toString())
        .filter((item: string) => item && item.trim() !== '');
      
      if (subsportList.length > 0) {
        normalized['data.subsports(multipleArrayContains)'] = subsportList.join(',');
      }
      delete normalized['data.subsport'];
    }

    return normalized;
  }

  async getEventList(pageNumber: number = 1, pageSize: number = 20, filters?: EventFilters) {
    let queryParams = `pageNumber=${pageNumber}&pageSize=${pageSize}`;

    const baseFilters = { };

    let combinedFilters: object = { ...baseFilters };
    if (filters && Object.keys(filters).length > 0) {
      const userFilters = filtersToBackendParams(filters);
      combinedFilters = { ...baseFilters, ...userFilters };
    }

    // CRITICAL: Ensure we ALWAYS have a country filter (kind)
    // If not provided by user filters, add default country
    if (!combinedFilters['kind']) {
      const defaultCountryId = filters?.country || config.app.defaultCountryId;
      combinedFilters['kind'] = `country:${defaultCountryId}`;
    }

    combinedFilters = this.getDefaultOrderByAdded(combinedFilters);

    // Normalizar filtros antes de enviar (convierte data.sport y data.subsport al formato correcto)
    const requestBody = this.normalizeFiltersForBackend(combinedFilters);

    return this.api.request<PaginatedResponse<Event>>({
      path: `/api/event/list?${queryParams}`,
      method: 'POST',
      security: false,
      async: true,
      body: requestBody
    });
  }

  async getPreferredEvents(
    pageNumber: number = 1,
    pageSize: number = 20,
    preferences: {
      countryId?: string;
      provinceIds?: number[];
      sports?: Array<{ sportId: number; categoryIds: number[] }>;
    },
    filters?: EventFilters,
    random: boolean = false,
    language?: string
  ) {
    let queryParams = `pageNumber=${pageNumber}&pageSize=${pageSize}`;

    const baseFilters = { };

    let combinedFilters: object = { ...baseFilters };
    if (filters && Object.keys(filters).length > 0) {
      const userFilters = filtersToBackendParams(filters, {
        country: true,
        provinces: true,
		sport: true,
		subsport: true
      });
      combinedFilters = { ...baseFilters, ...userFilters };
    }

	if (preferences.sports && preferences.sports.length > 0) {
		// Construimos un filtro (or) con un grupo por deporte. Cada grupo es un sub-filtro cuyas
		// condiciones se combinan con AND en el backend, y los grupos entre sí con OR. Así cada
		// subdeporte queda atado a SU deporte (data.sport = X AND data.subsports contiene ...),
		// evitando que un deporte sin categorías arrastre el filtro de subsports de otro deporte
		// o que una categoría homónima traiga eventos del deporte equivocado.
		const orGroups: Array<Record<string, any>> = [];

		for (const sport of preferences.sports) {
			const sportName = await this.convertToEnglishSportName(sport.sportId.toString());
			const group: Record<string, any> = { 'data.sport': sportName };

			if (sport.categoryIds && sport.categoryIds.length > 0) {
				const subSportNames = await this.convertToEnglishSubSportName(
					sport.categoryIds.map(id => id.toString()),
					sport.sportId.toString()
				);
				const cleanedSubsports = subSportNames
					.map(s => s?.toString())
					.filter((s): s is string => !!s && s.trim() !== '');

				if (cleanedSubsports.length > 0) {
					group['data.subsports(multipleArrayContains)'] = cleanedSubsports.join(',');
				}
			}

			orGroups.push(group);
		}

		if (orGroups.length > 0) {
			combinedFilters['(or)'] = orGroups;
		}
	}

    if (preferences.countryId && preferences.countryId !== 'all') {
      combinedFilters['kind'] = `country:${preferences.countryId}`;
    } else if (!combinedFilters['kind']) {
      const defaultCountryId = filters?.country || config.app.defaultCountryId;
      combinedFilters['kind'] = `country:${defaultCountryId}`;
    }

    if (preferences.provinceIds && preferences.provinceIds.length > 0) {
      combinedFilters['data.location.provinceId'] = preferences.provinceIds;
    }

    combinedFilters = this.getDefaultOrderByAdded(combinedFilters);

    // Normalizar filtros antes de enviar (convierte data.sport y data.subsport al formato correcto)
    const requestBody = this.normalizeFiltersForBackend(combinedFilters);

    // Determinar el endpoint según el parámetro random
    const endpoint = random && language
      ? `/api/event/preferred/random?${queryParams}`
      : `/api/event/preferred?${queryParams}`;

    return this.api.request<PaginatedResponse<Event>>({
      path: endpoint,
      method: 'POST',
      security: true,
      async: true,
      body: requestBody
    });
  }

  async getFreshEvents(language: string, countryId?: string) {
    const effectiveCountryId = countryId || this.getCountryId();
    const path = `/api/event/${language}/fresh/${effectiveCountryId}`;

    return this.api.request<PaginatedResponse<Event>>({
      path: path,
      security: false,
      async: true
    });
  }

  async getTendencyEvents(language: string, from: number, to: number, countryId?: string) {
    const baseParams = `from=${from}&to=${to}`;
    const effectiveCountryId = countryId || this.getCountryId();
    const countryParam = `&countryId=${effectiveCountryId}`;
    const path = `/api/event/${language}/tendency?${baseParams}${countryParam}`;

    return this.api.request<PaginatedResponse<Event>>({
      path: path,
      security: false,
      async: true
    });
  }

  async getEventsByCategory(
    language: string,
    categoryId: string,
    pageNumber: number = 1,
    pageSize: number = 20,
    subCategoryIds?: string[],
    random?: boolean,
    filters?: EventFilters
  ) {
    const englishCategory = await this.convertToEnglishSportName(categoryId);
    let englishSubCategories = []

    if (subCategoryIds) {
      englishSubCategories = await this.convertToEnglishSubSportName(subCategoryIds, categoryId);
    }

    let queryParams = `pageNumber=${pageNumber}&pageSize=${pageSize}`;

    let url = `/api/event/${language}/category/${encodeURIComponent(englishCategory)}`

    if (random) {
      url += '/random'
    }

    const baseFilters = { };

    let combinedFilters: object = { ...baseFilters };
    if (filters && Object.keys(filters).length > 0) {
      const userFilters = filtersToBackendParams(filters);
      combinedFilters = { ...combinedFilters, ...userFilters };
    }

    // CRITICAL: Ensure we ALWAYS have a country filter (kind)
    // If not provided by user filters, add default country
    if (!combinedFilters['kind']) {
      const defaultCountryId = filters?.country || config.app.defaultCountryId;
      combinedFilters['kind'] = `country:${defaultCountryId}`;
    }

    combinedFilters = this.getDefaultOrderByAdded(combinedFilters);

    if (englishSubCategories.length > 0) {
      combinedFilters["data.subsports(multipleArrayContains)"] = englishSubCategories.join(',');
    }

    if (englishCategory) {
      combinedFilters["data.sport"] = englishCategory;
    }

    // Normalizar filtros antes de enviar (convierte data.sport y data.subsport al formato correcto)
    const requestBody = this.normalizeFiltersForBackend(combinedFilters);

    return this.api.request<PaginatedResponse<Event>>({
      path: `${url}?${queryParams}`,
      method: 'POST',
      security: false,
      async: true,
      body: requestBody
    });
  }

  async getEventByAlias(alias: string, countryId?: string, calledFromChatbot: boolean = false, trackingHeaders?: Record<string, string>, organizerAuthToken?: string) {
    // Use provided countryId from cookie/param, or get from localStorage (client), or use default
    const effectiveCountryId = countryId || this.getCountryId();
    // En SSR el ApiService no tiene acceso a localStorage; el caller puede pasar el JWT del
    // organizador (cookie HTTP) como `organizerAuthToken` para que el BE active el bypass.
    const headers = organizerAuthToken
      ? { ...(trackingHeaders ?? {}), Authorization: `Bearer ${organizerAuthToken}` }
      : trackingHeaders;
    return this.api.request<EventPublicInfo>({
      path: `/api/event/alias/${alias}?countryId=${effectiveCountryId}&calledFromChatbot=${calledFromChatbot}`,
      security: false,
      async: true,
      extraHeaders: headers
    });
  }

  // [devcontrol-004] Caché TTL del flag devControl: el mismo evento se consulta desde varios puntos (mount de
  // EventCard, guard SSR del register layout, click en el dashboard) y el flag cambia muy
  // rara vez. Se cachea la PROMESA para deduplicar también llamadas concurrentes. En server
  // el singleton sobrevive entre requests → el TTL evita servir un flag stale indefinidamente.
  private newFlowCache = new Map<string, { promise: Promise<boolean>; ts: number }>();
  private static readonly NEW_FLOW_CACHE_TTL_MS = 60_000;

  /**
   * [devcontrol-004] Feature flag por evento (contenedor Cosmos public/devControl, doc devId=004): ¿usa este
   * evento el flujo nuevo bajo el árbol público /event/*? Si no, la inscripción / mi
   * inscripción / clasificaciones siguen viajando al WebRTS legacy (www.rockthesport.com).
   * GET /api/site/event/dev-control/{devId}/{eventId} — el BKD ejecuta la query
   * (enabled=true + eventId como string dentro del array `allowed`).
   * Fail-closed: cualquier error de red o respuesta inesperada → false (camino legacy).
   */
  async isEventOnNewFlow(eventId: number | string): Promise<boolean> {
    // Bypass de desarrollo: en dev el flag se considera siempre activo (ver lib/dev-control).
    if (isDevControlBypassed()) return true;
    const key = String(eventId);
    const cached = this.newFlowCache.get(key);
    if (cached && Date.now() - cached.ts < EventService.NEW_FLOW_CACHE_TTL_MS) {
      return cached.promise;
    }
    const attempt = () =>
      this.api
        .request<{ allowed: boolean }>({
          path: `/api/site/event/dev-control/${NEW_EVENT_FLOW_DEV_ID}/${encodeURIComponent(key)}`,
          security: false,
          async: true,
        })
        .then(res => res.data?.allowed === true);
    // Un reintento único antes del fail-closed: un blip transitorio no debe expulsar del
    // árbol /event/* a un evento habilitado (el gate del layout redirige con false).
    const promise = attempt()
      .catch(() => attempt())
      .catch(() => {
        // Fail-closed sin envenenar la caché: el próximo caller reintenta.
        this.newFlowCache.delete(key);
        return false;
      });
    this.newFlowCache.set(key, { promise, ts: Date.now() });
    return promise;
  }

  /**
   * Equivalente a getEventByAlias pero contra el endpoint de Site (`/api/site/event/alias/{alias}`),
   * que acepta `lang` y sirve el doc del evento mergeado con la copia traducida cuando existe.
   * Uso exclusivo del árbol `/[lang]/site/[eventAlias]/*`.
   */
  async getSiteEventByAlias(alias: string, lang: string, countryId?: string, calledFromChatbot: boolean = true, trackingHeaders?: Record<string, string>, organizerAuthToken?: string, managerToken?: string) {
    const effectiveCountryId = countryId || this.getCountryId();
    // managerToken: JWT del manager (cookie rts_mgr_token) en cabecera propia X-Manager-Token. Permite al
    // BE servir un evento en borrador al organizador en la PRIMERA visita, antes de que exista el token
    // de organizador del exchange (que necesita el eventId que devuelve esta misma llamada).
    let headers = trackingHeaders;
    if (organizerAuthToken) headers = { ...(headers ?? {}), Authorization: `Bearer ${organizerAuthToken}` };
    if (managerToken) headers = { ...(headers ?? {}), 'X-Manager-Token': managerToken };
    return this.api.request<EventPublicInfo>({
      path: `/api/site/event/alias/${alias}?countryId=${effectiveCountryId}&calledFromChatbot=${calledFromChatbot}&lang=${lang}`,
      security: false,
      async: true,
      extraHeaders: headers
    });
  }

  /**
   * Devuelve la availability del microservicio de seat-reservation para un evento.
   * - 200 con data → { available: true, data }.
   * - 404 → { available: false, reason: 'not_initialized' } (evento no migrado al microservicio).
   * - red caída / 5xx → { available: false, reason: 'service_down' }.
   * El status nunca lanza: el caller pinta un fallback si available=false.
   */
  async getSeatAvailability(eventId: number | string): Promise<SeatAvailabilityEnvelope> {
    try {
      const res = await this.api.request<SeatAvailability>({
        path: `/api/site/registration/event/${encodeURIComponent(String(eventId))}/availability`,
        security: false,
        async: true,
      });
      if (res.statusCode === 404) return { available: false, reason: 'not_initialized' };
      if (res.data) return { available: true, data: res.data };
      return { available: false, reason: 'service_down' };
    } catch {
      return { available: false, reason: 'service_down' };
    }
  }

  // Disponibilidad por opción de pregunta (talla camiseta etc.) — fuente: SeatReservation stock
  // module. 404 = el evento no tiene stocks inicializados en el admin dashboard ⇒ el FE debe
  // pintar todas las opciones como disponibles (no hay stock-tracking para este evento).
  async getStockAvailability(eventId: number | string): Promise<StockAvailabilityEnvelope> {
    try {
      const res = await this.api.request<StockAvailability>({
        path: `/api/site/registration/event/${encodeURIComponent(String(eventId))}/stock-availability`,
        security: false,
        async: true,
      });
      if (res.statusCode === 404) return { available: false, reason: 'not_initialized' };
      if (res.data) return { available: true, data: res.data };
      return { available: false, reason: 'service_down' };
    } catch {
      return { available: false, reason: 'service_down' };
    }
  }

  /**
   * Notifica al backend que el usuario ha pulsado "Inscribirse" en un evento.
   * El backend dispara el evento InitiateCheckout en la Facebook Conversions API.
   *
   * Se llama desde el cliente (browser → backend directo), por lo que el backend
   * recibe la IP y el User-Agent reales sin necesidad de reenvío.
   * El JWT se incluye automáticamente si el usuario está autenticado (security: true).
   *
   * Es fire-and-forget: no bloquea la apertura de la ventana de inscripción.
   */
  /**
   * Notifica al backend que el usuario ha realizado una búsqueda en la SearchBar.
   * El backend dispara el evento Search en la Facebook Conversions API.
   *
   * Llamada directa browser → backend: el backend recibe la IP y el User-Agent reales.
   * El JWT se incluye automáticamente si el usuario está autenticado (security: true).
   *
   * Es fire-and-forget: no bloquea la visualización de los resultados de búsqueda.
   */
  async trackSearch(
    searchString: string,
    fbp?: string,
    fbc?: string,
    eventSourceUrl?: string,
    advertisementConsent?: boolean
  ): Promise<void> {
    try {
      await this.api.request<void>({
        path: '/api/facebook-capi/track-search',
        method: 'POST',
        security: true,
        body: {
          searchString,
          fbp: fbp || null,
          fbc: fbc || null,
          eventSourceUrl: eventSourceUrl || null,
          advertisementConsent: advertisementConsent ?? false,
        },
      });
    } catch {
      // Fire-and-forget: ignorar errores para no interrumpir el flujo del usuario
    }
  }

  /**
   * Notifica al backend que el usuario ha entrado en la página de detalle de un evento.
   * El backend dispara el evento ViewContent en la Facebook Conversions API.
   *
   * Llamada directa browser → backend: el backend recibe la IP y el User-Agent reales.
   * El JWT se incluye automáticamente si el usuario está autenticado (security: true).
   *
   * Es fire-and-forget: no bloquea la carga de la página.
   */
  async trackViewContent(
    alias: string,
    countryId?: string,
    fbp?: string,
    fbc?: string,
    eventSourceUrl?: string,
    advertisementConsent?: boolean
  ): Promise<void> {
    try {
      await this.api.request<void>({
        path: '/api/facebook-capi/track-view-content',
        method: 'POST',
        security: true,
        body: {
          alias,
          countryId: parseInt(countryId || this.getCountryId(), 10),
          fbp: fbp || null,
          fbc: fbc || null,
          eventSourceUrl: eventSourceUrl || null,
          advertisementConsent: advertisementConsent ?? false,
        },
      });
    } catch {
      // Fire-and-forget: ignorar errores para no interrumpir el flujo del usuario
    }
  }

  async trackInitiateCheckout(
    alias: string,
    countryId?: string,
    fbp?: string,
    fbc?: string,
    eventSourceUrl?: string,
    advertisementConsent?: boolean
  ): Promise<void> {
    try {
      await this.api.request<void>({
        path: '/api/facebook-capi/track-initiate-checkout',
        method: 'POST',
        security: true,
        body: {
          alias,
          countryId: parseInt(countryId || this.getCountryId(), 10),
          fbp: fbp || null,
          fbc: fbc || null,
          eventSourceUrl: eventSourceUrl || null,
          advertisementConsent: advertisementConsent ?? false,
        },
      });
    } catch {
      // Fire-and-forget: ignorar errores para no interrumpir el flujo del usuario
    }
  }

  /**
   * Notifica al backend que una inscripción se ha completado con pago.
   * El backend dispara el evento Purchase en la Facebook Conversions API, cerrando el embudo
   * ViewContent → InitiateCheckout → Purchase.
   *
   * El importe, el número de inscritos del carrito y el orderId NO se envían desde aquí: el
   * backend los deriva del documento Cosmos de la reserva (`reservationId`), con la misma
   * fórmula que usan los initiate de pasarela para calcular lo que se cobra de verdad. Es la
   * única forma de que lo que llega a Facebook no dependa de que el cliente sume bien, y de
   * que una reserva que no llegó a cobrarse no se reporte como compra.
   *
   * El backend deduplica por `purchase-{reservationId}`, así que recargar el justificante no
   * cuenta la compra dos veces.
   *
   * Llamada directa browser → backend: el backend recibe la IP y el User-Agent reales.
   * Es fire-and-forget: no bloquea el pintado del justificante.
   */
  async trackPurchase(
    alias: string,
    reservationId: string,
    countryId?: string,
    fbp?: string,
    fbc?: string,
    eventSourceUrl?: string,
    advertisementConsent?: boolean
  ): Promise<void> {
    try {
      await this.api.request<void>({
        path: '/api/facebook-capi/track-purchase',
        method: 'POST',
        security: true,
        body: {
          alias,
          reservationId,
          countryId: parseInt(countryId || this.getCountryId(), 10),
          fbp: fbp || null,
          fbc: fbc || null,
          eventSourceUrl: eventSourceUrl || null,
          advertisementConsent: advertisementConsent ?? false,
        },
      });
    } catch {
      // Fire-and-forget: ignorar errores para no interrumpir el flujo del usuario
    }
  }

  async getEventsByIds(eventIds: string[]): Promise<{ items: Event[] }> {
    if (!eventIds || eventIds.length === 0) {
      return { items: [] };
    }

    const response = await this.api.request<{ items: Event[] }>({
      path: `/api/event/idList`,
      method: 'POST',
      security: false,
      async: true,
      body: eventIds
    });

    return response.data || { items: [] };
  }

  // Caché por evento de los píxeles de Meta del micrositio (`[meta-pixel-pageview]`). Se pide en
  // cada cambio de página del árbol del evento (portada → cuestionario → cesta → justificante) y
  // la configuración solo cambia cuando el organizador guarda en el Manager. Se cachea la PROMESA
  // para que dos montajes simultáneos compartan la petición; un fallo no se cachea.
  private facebookPixelsCache = new Map<number, { promise: Promise<EventFacebookPixel[]>; ts: number }>();
  private static readonly FACEBOOK_PIXELS_TTL_MS = 5 * 60_000;

  /**
   * Píxeles de Meta a los que mandar el PageView en el micrositio del evento, con sus páginas.
   * Nunca lanza: ante cualquier error devuelve `[]` — es tracking y no puede romper la página.
   */
  getEventFacebookPixels(eventId: number): Promise<EventFacebookPixel[]> {
    if (!Number.isFinite(eventId) || eventId <= 0) return Promise.resolve([]);

    const cached = this.facebookPixelsCache.get(eventId);
    if (cached && Date.now() - cached.ts < EventService.FACEBOOK_PIXELS_TTL_MS) return cached.promise;

    const promise = this.api
      .request<EventFacebookPixel[]>({
        path: `/api/event/${eventId}/facebook-pixels`,
        security: false,
        async: true,
      })
      .then(response => {
        // Sin configuración el BE devuelve `[]`; cualquier otra cosa es un error que no se cachea.
        if (Array.isArray(response.data)) return response.data;
        this.facebookPixelsCache.delete(eventId);
        return [];
      })
      .catch(() => {
        this.facebookPixelsCache.delete(eventId);
        return [] as EventFacebookPixel[];
      });

    this.facebookPixelsCache.set(eventId, { promise, ts: Date.now() });
    return promise;
  }

  // Contenedores GTM del organizador ([organizer-gtm]): mismo patrón de caché que los píxeles de
  // Meta — el componente se remonta en cada página del micrositio y la config solo cambia cuando
  // RTS la guarda en la pantalla interna Management RTS > GTM.
  private gtmContainersCache = new Map<number, { promise: Promise<string[]>; ts: number }>();
  private static readonly GTM_CONTAINERS_TTL_MS = 5 * 60_000;

  /**
   * IDs de los contenedores de Google Tag Manager del organizador del evento para su micrositio
   * (los de la pantalla interna Management RTS > GTM). El BKD los devuelve ya validados y
   * deduplicados; `[]` si no hay o si falla.
   */
  getEventGtmContainers(eventId: number): Promise<string[]> {
    if (!Number.isFinite(eventId) || eventId <= 0) return Promise.resolve([]);

    const cached = this.gtmContainersCache.get(eventId);
    if (cached && Date.now() - cached.ts < EventService.GTM_CONTAINERS_TTL_MS) return cached.promise;

    const promise = this.api
      .request<string[]>({
        path: `/api/event/${eventId}/gtm-containers`,
        security: false,
        async: true,
      })
      .then(response => {
        if (Array.isArray(response.data)) return response.data;
        this.gtmContainersCache.delete(eventId);
        return [];
      })
      .catch(() => {
        this.gtmContainersCache.delete(eventId);
        return [] as string[];
      });

    this.gtmContainersCache.set(eventId, { promise, ts: Date.now() });
    return promise;
  }

  // Caché por evento de la ocupación de plazas. Existe porque una sola página monta varios
  // listados a la vez (la home tiene N secciones, y buscador/recomendados repiten eventos) y cada
  // uno pediría su lote por separado; detrás, el SP escanea la tabla `perf.Inscripcion{id}` de
  // cada evento con límite de aforo. TTL corto: el recuento cambia solo mientras la gente se
  // inscribe, y un minuto de desfase en un badge es irrelevante frente a repetir el escaneo.
  // Se cachean también los ids que el BE no devuelve (como null) para no reintentarlos en bucle.
  private registrationAvailabilityCache = new Map<number, { value: EventRegistrationAvailability | null; ts: number }>();
  private static readonly REGISTRATION_AVAILABILITY_TTL_MS = 60_000;

  /**
   * Ocupación de plazas de un lote de eventos, para que las tarjetas de los listados distingan
   * "abiertas" / "cerradas" / "agotadas" y no solo por fechas de inscripción.
   *
   * Un único round-trip por lote (solo con los ids que no estén cacheados): el BKD resuelve todos
   * los eventos con una sola consulta SQL. Devuelve un mapa por eventId; los ids que el BE no
   * reconozca simplemente no aparecen y el consumidor degrada a la lógica de solo fechas
   * (ver `resolveEventRegistrationState`).
   *
   * La ficha de evento NO usa esto: recibe el bloque ya embebido en
   * `EventPublicInfo.registrationAvailability`.
   */
  async getEventsRegistrationAvailability(eventIds: number[]): Promise<Record<number, EventRegistrationAvailability>> {
    const ids = Array.from(new Set((eventIds || []).filter(id => Number.isFinite(id) && id > 0)));
    if (ids.length === 0) return {};

    const now = Date.now();
    const ttl = EventService.REGISTRATION_AVAILABILITY_TTL_MS;
    const result: Record<number, EventRegistrationAvailability> = {};
    const missing: number[] = [];

    for (const id of ids) {
      const cached = this.registrationAvailabilityCache.get(id);
      if (cached && now - cached.ts < ttl) {
        if (cached.value) result[id] = cached.value;
      } else {
        missing.push(id);
      }
    }

    if (missing.length === 0) return result;

    const response = await this.api.request<EventRegistrationAvailability[]>({
      path: `/api/event/registration-availability`,
      method: 'POST',
      security: false,
      async: true,
      body: missing
    });

    const fetched = new Map<number, EventRegistrationAvailability>();
    for (const row of response.data ?? []) {
      if (row?.eventId) fetched.set(row.eventId, row);
    }

    const ts = Date.now();
    for (const id of missing) {
      const row = fetched.get(id) ?? null;
      this.registrationAvailabilityCache.set(id, { value: row, ts });
      if (row) result[id] = row;
    }

    return result;
  }

  async getAvailableSports() {
    return this.api.request<Deporte[]>({
      path: `/api/event/sports/available`,
      security: false,
      async: true
    });
  }

  async getAvailableCountries(filters?: EventFilters) {
    if (filters && Object.keys(filters).length > 0) {
      const backendFilters = filtersToBackendParams(filters as EventFilters, {
        sort: true,
        country: true,
        provinces: true
      });

      if (filters.sport) {
        const englishSport = await this.convertToEnglishSportName(filters.sport);
        backendFilters["data.sport"] = englishSport;
      }

      if (filters.categories && filters.categories.length > 0) {
        const englishCategories = await this.convertToEnglishSubSportName(filters.categories, filters.sport);
        backendFilters["data.subsports(multipleArrayContains)"] = englishCategories.join(',');
      }

      // Normalizar filtros antes de enviar (convierte data.sport y data.subsport al formato correcto)
      const requestBody = this.normalizeFiltersForBackend(backendFilters);

      return this.api.request<any>({
        path: `/api/event/countries`,
        method: 'POST',
        security: false,
        async: true,
        body: requestBody
      });
    } else {
      return this.api.request<any>({
        path: `/api/event/countries`,
        security: false,
        async: true
      });
    }
  }

  async getAvailableProvinces(filters?: EventFilters) {
    if (filters && Object.keys(filters).length > 0) {
      const backendFilters = filtersToBackendParams(filters as EventFilters, {
        sort: true,
        provinces: true
      });

      if (filters.sport) {
        const englishSport = await this.convertToEnglishSportName(filters.sport);
        backendFilters["data.sport"] = englishSport;
      }

      if (filters.categories && filters.categories.length > 0) {
        const englishCategories = await this.convertToEnglishSubSportName(filters.categories, filters.sport);
        backendFilters["data.subsports(multipleArrayContains)"] = englishCategories.join(',');
      }

      // Normalizar filtros antes de enviar (convierte data.sport y data.subsport al formato correcto)
      const requestBody = this.normalizeFiltersForBackend(backendFilters);

      return this.api.request<any>({
        path: `/api/event/provinces`,
        method: 'POST',
        security: false,
        async: true,
        body: requestBody
      });
    } else {
      return this.api.request<any>({
        path: `/api/event/provinces`,
        security: false,
        async: true
      });
    }
  }

  async sendContactEmail(language: string, eventId: number, contactData: {
    eventId: number;
    eventTitle: string;
    subject: string;
    category: string;
    message: string;
    senderName: string;
    senderEmail: string;
    countryId?: number;
    language?: string;
  }) {
    return this.api.request({
      path: `/api/event/${language}/contact/${eventId}`,
      method: 'POST',
      security: false,
      async: true,
      body: contactData
    });
  }
} 